"use client";

import Link from "@/components/locale-link";
import {
  HINT,
  RESULT_CARD,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import type { CITATION_STYLE_IDENTIFIER_STRINGS } from "@/components/tools/citation-style-identifier";
import {
  Badge,
  type BadgeTone,
} from "@/components/tools/reference-tools/badge";
import { useToolLocale, useToolNumber } from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import type {
  Confidence,
  ListVerdict,
  LookalikePair,
  StyleId,
  Verdict,
} from "@/lib/tool-kit/reference-tools/style-identifier";

/*
 * The result panels of the Citation Style Identifier. Every word comes from the
 * widget's strings, passed in.
 */

export type IdentifierStrings = typeof CITATION_STYLE_IDENTIFIER_STRINGS;
type Strings = IdentifierStrings;
type Family = keyof Strings["generators"];
type Comparison = keyof Strings["comparisons"];

const FAMILY: Record<StyleId, Family> = {
  apa: "apa",
  mla: "mla",
  "chicago-nb": "chicago",
  "chicago-ad": "chicago",
  harvard: "harvard",
  ieee: "ieee",
  vancouver: "vancouver",
  ama: "ama",
};

const CHECKER_HREF: Partial<Record<Family, string>> = {
  apa: "/tools/citation-checker",
  mla: "/tools/citation-checker-mla",
  chicago: "/tools/citation-checker-chicago",
  harvard: "/tools/citation-checker-harvard",
};

/** Guides that compare exactly the two styles of a lookalike pair. */
const COMPARE_HREF: Record<Comparison, string> = {
  "apa-harvard": "/guides/harvard-vs-apa",
  "vancouver-ama": "/guides/ama-vs-vancouver",
  "mla-chicago": "/guides/mla-vs-chicago",
  "apa-chicago": "/guides/apa-vs-chicago",
};

const TONE: Record<Confidence, BadgeTone> = {
  high: "good",
  medium: "info",
  low: "warn",
  none: "neutral",
};

const LINK = "text-primary underline-offset-4 hover:underline";

export function styleLabel(
  verdict: { tied: StyleId[] },
  strings: Strings
): string {
  return verdict.tied.map((style) => strings.styles[style]).join(strings.or);
}

function evidenceText(code: string, strings: Strings): string {
  return (strings.evidence as Record<string, string>)[code] ?? code;
}

export function EvidenceList({
  verdict,
  strings,
}: {
  verdict: Verdict;
  strings: Strings;
}) {
  return (
    <ul className="mt-2 list-disc space-y-1 pl-5 text-muted-foreground text-xs">
      {verdict.evidence.map((item) => (
        <li key={item.code}>{evidenceText(item.code, strings)}</li>
      ))}
    </ul>
  );
}

function StyleLinks({ style, strings }: { style: StyleId; strings: Strings }) {
  const family = FAMILY[style];
  const checker = CHECKER_HREF[family];
  const checkerName = (strings.checkers as Record<string, string>)[family];
  return (
    <ul className="space-y-1 text-sm">
      {checker && checkerName ? (
        <li>
          <Link className={LINK} href={checker}>
            {t(strings.checkerLink, { name: checkerName })}
          </Link>
        </li>
      ) : null}
      <li>
        <Link className={LINK} href={`/${family}`}>
          {t(strings.generatorLink, { name: strings.generators[family] })}
        </Link>
      </li>
    </ul>
  );
}

function Lookalike({
  pair,
  strings,
}: {
  pair: LookalikePair;
  strings: Strings;
}) {
  const comparison = pair in COMPARE_HREF ? (pair as Comparison) : null;
  return (
    <div className="space-y-1 text-sm">
      <p className="text-foreground">{strings.lookalikes[pair]}</p>
      {comparison ? (
        <Link className={LINK} href={COMPARE_HREF[comparison]}>
          {t(strings.compareLink, { name: strings.comparisons[comparison] })}
        </Link>
      ) : null}
    </div>
  );
}

export function VerdictCard({
  list,
  count,
  strings,
}: {
  list: ListVerdict;
  count: number;
  strings: Strings;
}) {
  const locale = useToolLocale();
  const fmt = useToolNumber();
  if (!list.top) {
    return null;
  }
  return (
    <div className={`${RESULT_CARD} space-y-4`}>
      <div className="space-y-1">
        <h2 className={WIDGET_H2}>{strings.verdictHeading}</h2>
        <p className="font-semibold text-2xl text-foreground">
          {styleLabel(list, strings)}
        </p>
        <div className="flex flex-wrap items-center gap-2 text-muted-foreground text-sm">
          <Badge tone={TONE[list.confidence]}>
            {strings.confidence[list.confidence]}
          </Badge>
          <span>
            {tPlural(strings.agreement, locale, count, {
              agreeing: fmt(list.agreeing),
              count: fmt(count),
            })}
          </span>
        </div>
        {list.tied.length > 1 ? (
          <p className="text-foreground text-sm">
            {t(strings.tooClose, { styles: styleLabel(list, strings) })}
          </p>
        ) : null}
        {list.outliers.length > 0 ? (
          <p className="text-muted-foreground text-sm">
            {tPlural(strings.outliersNote, locale, list.outliers.length, {
              count: fmt(list.outliers.length),
            })}
          </p>
        ) : null}
      </div>
      {list.lookalike ? (
        <Lookalike pair={list.lookalike} strings={strings} />
      ) : null}
      <StyleLinks strings={strings} style={list.top} />
    </div>
  );
}

export function EvidenceSplit({
  list,
  strings,
}: {
  list: ListVerdict;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  return (
    <div className="space-y-3">
      <h3 className={WIDGET_H3}>{strings.splitHeading}</h3>
      <ul className="space-y-2">
        {list.ranking.slice(0, 5).map((entry) => (
          <li className="space-y-1" key={entry.style}>
            <div className="flex justify-between gap-3 text-sm">
              <span className="text-foreground">
                {strings.styles[entry.style]}
              </span>
              <span className="text-muted-foreground">
                {t(strings.share, {
                  share: fmt(entry.share, {
                    style: "percent",
                    maximumFractionDigits: 0,
                  }),
                })}
              </span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-muted">
              <div
                className="h-full rounded-full bg-primary"
                style={{ width: `${Math.round(entry.share * 100)}%` }}
              />
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function ReferenceRow({
  verdict,
  outlier,
  strings,
}: {
  verdict: Verdict;
  outlier: boolean;
  strings: Strings;
}) {
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const count = verdict.evidence.length;
  return (
    <li className="space-y-2 rounded-lg border border-border bg-card p-3">
      <p className="break-words text-foreground text-sm">{verdict.text}</p>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="font-medium text-foreground">
          {verdict.top ? styleLabel(verdict, strings) : strings.confidence.none}
        </span>
        <Badge tone={TONE[verdict.confidence]}>
          {strings.confidence[verdict.confidence]}
        </Badge>
        {outlier ? <Badge tone="warn">{strings.outlier}</Badge> : null}
      </div>
      {count === 0 ? (
        <p className={HINT}>{strings.noClues}</p>
      ) : (
        <details>
          <summary className="cursor-pointer text-muted-foreground text-xs">
            {tPlural(strings.clues, locale, count, { count: fmt(count) })}
          </summary>
          <EvidenceList strings={strings} verdict={verdict} />
        </details>
      )}
    </li>
  );
}
